import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
  CSSProperties,
} from "react";
import {
  useFloating,
  autoUpdate,
  useDismiss,
  limitShift,
  useRole,
  useClick,
  useFocus,
  useInteractions,
  FloatingFocusManager,
  FloatingPortal,
  Placement,
  size,
  flip,
  shift,
  offset,
  useTransitionStyles,
} from "@floating-ui/react";

export type OpenOptions = {
  /** Optional position rectangle to use instead of the triggerRef. Takes precedence over triggerRef. */
  positionRect?: {
    x: number;
    y: number;
    width?: number;
    height?: number;
  };
};

export type CpFloatingDropdownHandle = {
  open: (options?: OpenOptions) => void;
  close: () => void;
  toggle: () => void;
  isOpen: boolean;
};

export type CpFloatingDropdownProps = {
  /** Function that renders the trigger element. First argument contains ref props that must be spread into the trigger element. */
  renderTrigger?: (
    refProps: Record<string, unknown> & {
      ref: (node: HTMLElement | null) => void;
    },
  ) => React.ReactNode;
  /** Function that renders the dropdown content [Handle is passed as an argument](/?path=/docs/components-cpfloatingdropdown--dropdown-handle) */
  renderContent: (handle: CpFloatingDropdownHandle) => React.ReactNode;
  /** Position of the dropdown relative to the trigger. */
  placement?: Placement;
  /** Width of the dropdown content. 'sm' (18rem), 'md' (28rem), 'lg' (40rem), 'block' (100% of trigger width), or a custom width in pixels (will be converted to rem). */
  contentWidth?: "sm" | "md" | "lg" | "block" | number;
  /** Maximum height of the dropdown content. Defaults to 'auto' or a custom max height in pixels (will be converted to rem). */
  contentHeight?: "auto" | number;
  /** Choose where to append the content. element - The element to append to. 'parent' - appends to the parent element of the dropdown. Default is document.body. If using a reference to an element make it default to null so that floating-ui knows to wait for the element to be mounted. */
  appendTo?: HTMLElement | "parent" | null;
  /** Function that is called when the dropdown is opened */
  onOpen?: () => void;
  /** Function that is called when the dropdown is closed */
  onClose?: () => void;
  /** Whether the dropdown should cover/overlap the reference trigger element */
  cover?: boolean;
  /** Whether to automatically focus the dropdown content when it opens. */
  autoFocus?: boolean;
  /** Whether the dropdown should open when the trigger element receives focus. */
  openOnTriggerFocus?: boolean;
};

const widthMap = {
  sm: "18rem",
  md: "28rem",
  lg: "40rem",
};

export const CpFloatingDropdown = forwardRef<
  CpFloatingDropdownHandle,
  CpFloatingDropdownProps
>(
  (
    {
      renderTrigger,
      renderContent,
      placement = "bottom-start",
      contentWidth = "sm",
      contentHeight = "auto",
      appendTo,
      onOpen,
      onClose,
      cover = false,
      autoFocus = true,
      openOnTriggerFocus = false,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const middleware = useMemo(
      () => [
        ...(cover
          ? [
              offset(({ rects, placement: middlewarePlacement }) => {
                if (
                  middlewarePlacement.includes("bottom") ||
                  middlewarePlacement.includes("top")
                ) {
                  return -rects.reference.height;
                } else if (
                  middlewarePlacement.includes("left") ||
                  middlewarePlacement.includes("right")
                ) {
                  return -rects.reference.width;
                }
                return 0;
              }),
            ]
          : []),
        flip({ padding: 8 }),
        shift({ padding: 8, limiter: limitShift() }),
        ...(contentWidth === "block"
          ? [
              size({
                apply({ rects, elements }) {
                  Object.assign(elements.floating.style, {
                    width: `${rects.reference.width}px`,
                  });
                },
              }),
            ]
          : []),
      ],
      [contentWidth, cover],
    );

    const { refs, floatingStyles, context, update } = useFloating({
      open: isOpen,
      onOpenChange: (open) => {
        setIsOpen(open);
        if (open) {
          onOpen?.();
        } else {
          onClose?.();
        }
      },
      whileElementsMounted: (reference, floating, update) => {
        // Only use autoUpdate for real DOM elements, not virtual elements
        if (reference instanceof Element) {
          return autoUpdate(reference, floating, update);
        }
        // For virtual elements, don't auto-update - position once and stay there
        return () => {};
      },
      placement,
      middleware,
    });

    const { isMounted, styles: transitionStyles } = useTransitionStyles(
      context,
      { duration: 200 },
    );

    const click = useClick(context);
    const focus = useFocus(context, { enabled: openOnTriggerFocus });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "dialog" });
    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      focus,
      dismiss,
      role,
    ]);

    const onOpenChange = context.onOpenChange;
    const toggle = useCallback(
      () => onOpenChange(!isOpen),
      [onOpenChange, isOpen],
    );
    const open = useCallback(
      (options?: OpenOptions) => {
        if (options?.positionRect) {
          const { x, y, width = 0, height = 0 } = options.positionRect;

          // Create a virtual element with the coordinates
          // https://floating-ui.com/docs/virtual-elements
          refs.setPositionReference({
            getBoundingClientRect: () => ({
              width,
              height,
              x,
              y,
              top: y,
              right: x + width,
              bottom: y + height,
              left: x,
            }),
          });

          onOpenChange(true);

          // Need to manually update position for virtual elements since autoUpdate is disabled
          requestAnimationFrame(() => update());
        } else {
          onOpenChange(true);
        }
      },
      [onOpenChange, refs, update],
    );
    const close = useCallback(() => onOpenChange(false), [onOpenChange]);

    const getPortalRoot = () => {
      if (appendTo === undefined) return undefined;
      if (appendTo === "parent") {
        const refElement = refs.reference.current;
        return refElement && "parentElement" in refElement
          ? refElement.parentElement
          : null;
      }
      return appendTo || null;
    };

    const contentStyle = useMemo((): CSSProperties => {
      let width = "auto";
      if (typeof contentWidth === "number") {
        width = `${contentWidth / 10}rem`;
      } else if (typeof contentWidth === "string" && contentWidth !== "block") {
        width = widthMap[contentWidth] || "auto";
      }

      let maxHeight = "auto";
      if (typeof contentHeight === "number") {
        maxHeight = `${contentHeight / 10}rem`;
      }

      return {
        width,
        maxHeight,
        boxShadow: "var(--cp-dropdown-box-shadow)",
        zIndex: 100001,
        ...floatingStyles,
        ...transitionStyles,
      };
    }, [contentWidth, contentHeight, floatingStyles, transitionStyles]);

    const handle = {
      open,
      close,
      toggle,
      isOpen,
    };
    useImperativeHandle(ref, () => handle);

    const refProps = {
      ref: refs.setReference,
      ...getReferenceProps(),
    };

    return (
      <>
        {renderTrigger?.(refProps)}
        <FloatingPortal root={getPortalRoot()}>
          {isMounted && (
            <FloatingFocusManager
              context={context}
              modal={false}
              initialFocus={autoFocus ? undefined : -1}
              returnFocus={!!autoFocus}
            >
              <div
                ref={refs.setFloating}
                style={contentStyle}
                className="bg-[var(--cp-color-card-bg)] rounded-[var(--cp-form-border-radius)] overflow-auto"
                {...getFloatingProps()}
              >
                {renderContent(handle)}
              </div>
            </FloatingFocusManager>
          )}
        </FloatingPortal>
      </>
    );
  },
);
